import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import ApiClient from "../api/ApiClient";
import { createChart } from "lightweight-charts";

export const Charts = () => {
  // suggestions are loaded asynchronously via react-select AsyncSelect
  const [selectedTicker, setSelectedTicker] = useState(null);

  // New filter / cycle mode state
  const [filterSource, setFilterSource] = useState("");
  const [filterSectorId, setFilterSectorId] = useState(null);
  const [filterIndustryId, setFilterIndustryId] = useState(null);
  const [filterTag, setFilterTag] = useState("");
  const [tickersList, setTickersList] = useState([]);
  const [currentTickerIndex, setCurrentTickerIndex] = useState(-1);
  const sectorsMapRef = useRef({});
  const industriesMapRef = useRef({});

  const [interval, setInterval] = useState("Day");
  const [aggregated, setAggregated] = useState(false);
  const [showTradingView, setShowTradingView] = useState(false);
  const tradingViewRef = useRef(null);
  const tradingViewWidgetRef = useRef(null);
  // We'll load time ranges dynamically based on visible chart range; no static From/To inputs

  // pagination controls removed — chart handles its own dynamic loading

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const selectedTickerRef = useRef(null);
  const fetchingRangeRef = useRef(false);
  const initialZoomRef = useRef(false);
  const preserveVisibleRef = useRef(false);

  // Latest-value mirrors so the mount-time effect below can stay
  // dependency-free (no `exhaustive-deps` warnings) while still seeing the
  // current values of `items`, `interval`, and `aggregated`.
  const itemsRef = useRef(items);
  const intervalRef = useRef(interval);
  const aggregatedRef = useRef(aggregated);
  const fetchRangeRef = useRef(null);

  async function loadOptions(inputValue, callback) {
    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }
    try {
      const body = { SearchTerm: inputValue, Limit: 20, Offset: 0 };
      const data = await ApiClient.searchTickerItems(body);
      const options = (data || []).map((d) => ({
        value: d,
        label: `${d.Code || ""}${d.Name ? " " + d.Name : ""} [${
          d.Source || ""
        }]`,
      }));
      callback(options);
    } catch (err) {
      console.error("Suggestions failed", err);
      callback([]);
    }
  }

  async function showData() {
    if (!selectedTicker) {
      alert("Please select a ticker");
      return;
    }
    // initial load: default to last year
    selectedTickerRef.current = selectedTicker;
    const nowMs = Date.now();
    const oneYearMs = 365 * 24 * 3600 * 1000;
    const fromMs = nowMs - oneYearMs;
    await fetchRange(selectedTicker, fromMs, nowMs);
  }

  // Helper: try to get current visible timeframe from the chart.
  function getCurrentVisibleRangeMs() {
    try {
      if (!chartRef.current) return null;
      const vr = chartRef.current.timeScale().getVisibleRange();
      if (!vr || !vr.from || !vr.to) return null;
      const fromSec = typeof vr.from === "number" ? vr.from : Math.floor(new Date(vr.from).getTime() / 1000);
      const toSec = typeof vr.to === "number" ? vr.to : Math.floor(new Date(vr.to).getTime() / 1000);
      return { fromMs: fromSec * 1000, toMs: toSec * 1000 };
    } catch (e) {
      return null;
    }
  }

  async function loadTickersForFilters() {
    try {
      const body = {
        Source: filterSource || undefined,
        Sectors: filterSectorId ? [filterSectorId] : undefined,
        Industries: filterIndustryId ? [filterIndustryId] : undefined,
        Tag: filterTag || undefined,
        Limit: 1000,
        Offset: 0,
      };
      const list = await ApiClient.searchTickerItems(body);
      setTickersList(list || []);
      // reset index if list empty
      if (!list || list.length === 0) {
        setCurrentTickerIndex(-1);
      } else {
        setCurrentTickerIndex(0);
      }
      return list || [];
    } catch (err) {
      console.error("Failed to load tickers for filters", err);
      setTickersList([]);
      setCurrentTickerIndex(-1);
      return [];
    }
  }

  async function cycleToIndex(idx) {
    if (!tickersList || tickersList.length === 0) return;
    const clamped = ((idx % tickersList.length) + tickersList.length) % tickersList.length;
    const t = tickersList[clamped];
    if (!t) return;
    setCurrentTickerIndex(clamped);
    setSelectedTicker(t);
    selectedTickerRef.current = t;
    // preserve visible timeframe if possible
    const vr = getCurrentVisibleRangeMs();
    if (vr && vr.fromMs && vr.toMs) {
      // tell items effect to preserve visible range when applying new data
      preserveVisibleRef.current = true;
      await fetchRange(t, vr.fromMs, vr.toMs);
    } else {
      preserveVisibleRef.current = false;
      initialZoomRef.current = false;
      await fetchRange(t, Date.now() - 365 * 24 * 3600 * 1000, Date.now());
    }
  }

  // Next / Prev helpers
  function nextTicker() {
    cycleToIndex(currentTickerIndex + 1);
  }
  function prevTicker() {
    cycleToIndex(currentTickerIndex - 1);
  }

  // Effects: load reference maps used in selects
  useEffect(() => {
    ApiClient.getSectorsMap().then((m) => (sectorsMapRef.current = m)).catch(() => {});
    ApiClient.getIndustriesMap().then((m) => (industriesMapRef.current = m)).catch(() => {});
  }, []);

  // Keep refs synchronised with the latest state so the mount-only effects
  // below can read current values without re-subscribing.
  useEffect(() => {
    itemsRef.current = items;
    intervalRef.current = interval;
    aggregatedRef.current = aggregated;
    fetchRangeRef.current = fetchRange;
  });

  // Load TradingView tv.js dynamically when user enables the TradingView panel
  useEffect(() => {
    if (!showTradingView) return;

    let scriptEl = document.querySelector('script[src="https://s3.tradingview.com/tv.js"]');
    let created = false;
    const ensureScript = () => {
      if (window.TradingView) return Promise.resolve();
      if (scriptEl) return new Promise((res, rej) => {
        scriptEl.addEventListener('load', res);
        scriptEl.addEventListener('error', rej);
      });
      scriptEl = document.createElement('script');
      scriptEl.src = 'https://s3.tradingview.com/tv.js';
      scriptEl.async = true;
      created = true;
      document.head.appendChild(scriptEl);
      return new Promise((res, rej) => {
        scriptEl.onload = res;
        scriptEl.onerror = rej;
      });
    };

    function initWidget() {
      try {
        if (!window.TradingView) return;
        const container = tradingViewRef.current;
        if (!container) return;

        // remove existing widget if any
        if (tradingViewWidgetRef.current && tradingViewWidgetRef.current.remove) {
          try { tradingViewWidgetRef.current.remove(); } catch (e) { /* ignore */ }
          tradingViewWidgetRef.current = null;
        }

        const symbol = selectedTicker ? (selectedTicker.Code || selectedTicker.Name) : 'BTCUSD';
        tradingViewWidgetRef.current = new window.TradingView.widget({
          container_id: container.id,
          autosize: true,
          symbol: symbol,
          interval: interval === 'Day' ? 'D' : interval === 'Hour' ? '60' : interval === 'Minute' ? '1' : 'D',
          timezone: 'Etc/UTC',
          theme: 'Light',
          style: '1',
          locale: 'en',
          toolbar_bg: '#f4f7f9',
          enable_publishing: false,
          allow_symbol_change: true,
          studies_overrides: {},
        });
      } catch (e) {
        console.error('TradingView init failed', e);
      }
    }

    ensureScript().then(initWidget).catch((e) => console.error('Failed loading TradingView script', e));

    return () => {
      // cleanup widget and optionally script
      try {
        if (tradingViewWidgetRef.current && tradingViewWidgetRef.current.remove) tradingViewWidgetRef.current.remove();
      } catch (e) {}
      tradingViewWidgetRef.current = null;
      if (created && scriptEl && scriptEl.parentNode) scriptEl.parentNode.removeChild(scriptEl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTradingView, selectedTicker, interval]);

  // When filters change, reload tickers
  useEffect(() => {
    loadTickersForFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSource, filterSectorId, filterIndustryId, filterTag]);

  // no auto-cycle
  function getWindowMsForInterval(intervalKey) {
    // window in number of candles to request * interval length
    const candles = 500; // request ~500 candles per load
    const secondsPerCandle =
      intervalKey === "Minute"
        ? 60
        : intervalKey === "FiveMinutes"
        ? 300
        : intervalKey === "QuarterHour"
        ? 900
        : intervalKey === "Hour"
        ? 3600
        : 86400; // Day
    return candles * secondsPerCandle * 1000;
  }

  function secondsPerCandle(intervalKey) {
    return intervalKey === "Minute"
      ? 60
      : intervalKey === "FiveMinutes"
      ? 300
      : intervalKey === "QuarterHour"
      ? 900
      : intervalKey === "Hour"
      ? 3600
      : 86400;
  }

  async function fetchRange(ticker, fromMs, toMs) {
    if (!ticker) return;
    // guard concurrent range fetches
    if (fetchingRangeRef.current) return;
    fetchingRangeRef.current = true;
    setLoading(true);
    try {
      const body = {
        TimeFrom: fromMs ? new Date(fromMs).toISOString() : null,
        TimeTo: toMs ? new Date(toMs).toISOString() : null,
        Limit: 10000,
      };
      // use searchCandles which returns { items, totalCount, raw }
      const resp = await ApiClient.searchCandles({
        TickerId: ticker.Id,
        Interval: intervalRef.current,
        Aggregated: aggregatedRef.current,
        ...body,
      });

      const dataItems = resp.Items;

      const newItems = (dataItems || []).map((c) => {
        // c is Candle instance
        const time = c.getUnixSeconds();
        return {
          time,
          open: Number(c.Open),
          high: Number(c.High),
          low: Number(c.Low),
          close: Number(c.Close),
        };
      });

      // merge into existing items, avoid duplicates by time
      const map = new Map();
      (itemsRef.current || []).forEach((it) => map.set(Number(it.time), it));
      newItems.forEach((it) => map.set(Number(it.time), it));
      const merged = Array.from(map.values()).sort(
        (a, b) => Number(a.time) - Number(b.time)
      );
      setItems(merged);
      // do not apply initial zoom here; it's handled when the chart is updated
      return newItems;
    } catch (err) {
      console.error("Loading candles failed", err);
      return [];
    } finally {
      fetchingRangeRef.current = false;
      setLoading(false);
    }
  }

  // initialize chart on mount
  useEffect(() => {
    const container = document.getElementById("chartContainer");
    if (!container) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 420,
      layout: { background: { color: "#ffffff" }, textColor: "#000" },
      rightPriceScale: { scaleMargins: { top: 0.1, bottom: 0.1 } },
      timeScale: { timeVisible: true, secondsVisible: false },
    });
    chartRef.current = chart;

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#4caf50",
      downColor: "#f44336",
      borderDownColor: "#f44336",
      borderUpColor: "#4caf50",
      wickDownColor: "#f44336",
      wickUpColor: "#4caf50",
    });
    candleSeriesRef.current = candleSeries;

    const handleResize = () => {
      if (!chartRef.current) return;
      chartRef.current.applyOptions({ width: container.clientWidth });
    };
    window.addEventListener("resize", handleResize);

    // subscribe to visible range changes for dynamic loading
    const unsubscribe = chart
      .timeScale()
      .subscribeVisibleTimeRangeChange(async (visibleRange) => {
        try {
          if (!visibleRange) return;
          if (!selectedTickerRef.current) return;

          const from = visibleRange.from;
          const to = visibleRange.to;
          // visibleRange values may be numbers (seconds) or date strings
          const visibleFromSec =
            typeof from === "number"
              ? from
              : Math.floor(new Date(from).getTime() / 1000);
          const visibleToSec =
            typeof to === "number"
              ? to
              : Math.floor(new Date(to).getTime() / 1000);

          // update visible range label
          const label = document.getElementById("visibleRangeLabel");
          if (label) {
            const fmt = (s) =>
              new Date(Number(s) * 1000)
                .toISOString()
                .replace("T", " ")
                .replace(".000Z", "");
            label.textContent = `${fmt(visibleFromSec)} → ${fmt(visibleToSec)}`;
          }

          if (!itemsRef.current || itemsRef.current.length === 0) return;
          const earliestSec = Number(itemsRef.current[0].time);
          const latestSec = Number(itemsRef.current[itemsRef.current.length - 1].time);
          const visibleSpan = visibleToSec - visibleFromSec || 1;
          const threshold = Math.max(1, Math.floor(visibleSpan * 0.25)); // 25% of visible span
          const currentInterval = intervalRef.current;
          const doFetch = fetchRangeRef.current;

          // if user scrolled near left edge, load earlier
          if (visibleFromSec < earliestSec + threshold) {
            const windowMs = getWindowMsForInterval(currentInterval);
            const newFromMs = earliestSec * 1000 - windowMs;
            const newToMs = earliestSec * 1000 - 1;
            if (doFetch) await doFetch(selectedTickerRef.current, newFromMs, newToMs);
          }

          // if user scrolled near right edge, load later
          if (visibleToSec > latestSec - threshold) {
            const windowMs = getWindowMsForInterval(currentInterval);
            const newFromMs = latestSec * 1000 + 1;
            const newToMs = latestSec * 1000 + windowMs;
            if (doFetch) await doFetch(selectedTickerRef.current, newFromMs, newToMs);
          }
        } catch (e) {
          // swallow errors from async scroller
          console.error("Visible range handler error", e);
        }
      });

    return () => {
      window.removeEventListener("resize", handleResize);
      if (unsubscribe) unsubscribe();
      if (chartRef.current) chartRef.current.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
    };
  }, []);

  // update chart when items change
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    // If items are already normalized (have numeric `time` and OHLC fields), use them directly.
    const first = (items || [])[0];
    if (
      first &&
      typeof first.time !== "undefined" &&
      typeof first.open !== "undefined"
    ) {
      try {
        candleSeriesRef.current.setData(items);
        if (chartRef.current) {
          // If preserveVisibleRef is set, do not alter visible range — keep user's view
          if (preserveVisibleRef.current) {
            preserveVisibleRef.current = false;
          } else {
            // On the very first data load after a user action, zoom to show ~100 candles
            if (!initialZoomRef.current && items.length > 0) {
              const lastSec = Number(items[items.length - 1].time);
              const secPer = secondsPerCandle(intervalRef.current || "Day");
              if (items.length >= 100) {
                const from = lastSec - 100 * secPer;
                const to = lastSec;
                try {
                  chartRef.current.timeScale().setVisibleRange({ from, to });
                } catch (e) {
                  // fallback to fitContent if setVisibleRange is not accepted
                  chartRef.current.timeScale().fitContent();
                }
              } else {
                chartRef.current.timeScale().fitContent();
              }
              initialZoomRef.current = true;
            } else {
              chartRef.current.timeScale().fitContent();
            }
          }
        }
      } catch (err) {
        console.error("Updating chart failed", err);
      }
      return;
    }

    // Otherwise, map API-style items into the normalized format.
    const data = (items || []).map((c) => {
      const timeVal = c.Time ?? c.time ?? c.TimeUTC ?? c.timeUtc;
      let time;

      if (typeof timeVal === "number") {
        // decide whether numeric value is seconds or milliseconds
        // heuristic: if > 1e12 it's ms, if > 1e9 it's seconds
        if (timeVal > 1e12) {
          time = Math.floor(timeVal / 1000);
        } else {
          time = timeVal; // assume seconds
        }
      } else if (typeof timeVal === "string") {
        const d = new Date(timeVal);
        if (!isNaN(d.getTime())) time = Math.floor(d.getTime() / 1000);
        else time = timeVal;
      } else {
        time = timeVal;
      }

      return {
        time,
        open: Number(c.Open ?? c.open ?? 0),
        high: Number(c.High ?? c.high ?? 0),
        low: Number(c.Low ?? c.low ?? 0),
        close: Number(c.Close ?? c.close ?? 0),
      };
    });

    try {
      candleSeriesRef.current.setData(data);
      if (chartRef.current) {
        if (preserveVisibleRef.current) {
          preserveVisibleRef.current = false;
        } else {
          chartRef.current.timeScale().fitContent();
        }
      }
    } catch (err) {
      console.error("Updating chart failed", err);
    }
  }, [items]);

  function renderChart() {
    // Always keep chart container in the DOM. Previously we returned a "Loading..."
    // placeholder which removed the container while a fetch was in-progress and
    // broke the chart instance (the chart stays attached to the removed DOM node).
    // Instead, render the container and show a simple overlay while loading.
    return (
      <div style={{ position: "relative", height: 420 }}>
        <div id="chartContainer" style={{ height: 420 }} />
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.6)",
              zIndex: 1000,
            }}
          >
            <div>
              <em>Loading...</em>
            </div>
          </div>
        )}
        {/* TradingView container - toggled by user */}
        {showTradingView && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <button className="btn btn-sm btn-outline-primary" onClick={() => addSMA()}>Add SMA(50)</button>
              <button className="btn btn-sm btn-outline-primary" onClick={() => addRSI()}>Add RSI(14)</button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => removeStudies()}>Remove Studies</button>
            </div>
            <div id="tradingviewContainer" ref={tradingViewRef} style={{ height: 560, width: '100%' }} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <h1>Charts</h1>

      <div className="mb-3" style={{ maxWidth: "900px" }}>
        {/* Filters: Async ticker select is primary filter */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 320 }}>
            <label className="col-form-label">Ticker (search)</label>
            <AsyncSelect
              cacheOptions
              loadOptions={loadOptions}
              defaultOptions={false}
              onChange={(opt) => {
                if (!opt) {
                  setSelectedTicker(null);
                  return;
                }
                setSelectedTicker(opt.value);
                initialZoomRef.current = false;
              }}
              value={
                selectedTicker
                  ? {
                      value: selectedTicker,
                      label: `${selectedTicker.Code || ""} - ${
                        selectedTicker.Name || ""
                      }`,
                    }
                  : null
              }
              styles={{
                menu: (provided) => ({
                  ...provided,
                  zIndex: 1000,
                  maxHeight: "300px",
                }),
              }}
            />
          </div>
          {/* existing filters moved next to ticker select */}
        </div>

        {/* New: filter & cycle controls */}
        <div className="mt-2" style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: 12 }}>
          <div style={{ minWidth: 180 }}>
            <label className="col-form-label">Source</label>
            <input className="form-control" value={filterSource} onChange={(e) => setFilterSource(e.target.value)} placeholder="Source (e.g. DataSource)" />
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="col-form-label">Sector</label>
            <select className="form-control" value={filterSectorId || ""} onChange={(e) => setFilterSectorId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">&lt;Any&gt;</option>
              {Object.entries(sectorsMapRef.current || {}).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="col-form-label">Industry</label>
            <select className="form-control" value={filterIndustryId || ""} onChange={(e) => setFilterIndustryId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">&lt;Any&gt;</option>
              {Object.entries(industriesMapRef.current || {}).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="col-form-label">Tag</label>
            <input className="form-control" value={filterTag} onChange={(e) => setFilterTag(e.target.value)} placeholder="Tag" />
          </div>

          <div style={{ display: "flex", gap: "8px", alignItems: "end" }}>
            <button className="btn btn-secondary" onClick={() => prevTicker()} disabled={tickersList.length === 0}>Prev</button>
            <button className="btn btn-secondary" onClick={() => nextTicker()} disabled={tickersList.length === 0}>Next</button>
          </div>
        </div>

        <div className="mt-2 d-flex" style={{ gap: "8px" }}>
          <select
            className="form-control"
            style={{ width: "250px" }}
            value={aggregated ? `${interval}_aggregated` : interval}
            onChange={(e) => {
              const value = e.target.value;
              if (value.endsWith('_aggregated')) {
                setInterval(value.replace('_aggregated', ''));
                setAggregated(true);
              } else {
                setInterval(value);
                setAggregated(false);
              }
            }}
          >
            <option value="Minute">1 min</option>
            <option value="FiveMinutes">5 min</option>
            <option value="FiveMinutes_aggregated">5 min (aggregated)</option>
            <option value="QuarterHour">15 min</option>
            <option value="QuarterHour_aggregated">15 min (aggregated)</option>
            <option value="Hour">1 hr</option>
            <option value="Hour_aggregated">1 hr (aggregated)</option>
            <option value="Day">Daily</option>
          </select>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label className="col-form-label">Visible range</label>
              <div
                id="visibleRangeLabel"
                style={{ paddingTop: 6, color: "#555" }}
              >
                Use mouse/touch to pan/zoom. Chart will load more data when you
                reach edges.
              </div>
            </div>
            {/* show currently selected ticker */}
            <div style={{ marginLeft: 16 }}>
              <label className="col-form-label">Current ticker</label>
              <div style={{ paddingTop: 6 }}>{selectedTicker ? `${selectedTicker.Code || ''} - ${selectedTicker.Name || ''}` : '<none>'}</div>
            </div>
            <div style={{ display: "flex", alignItems: "end" }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={showData}>
                  Load Initial
                </button>
                <button
                  className={"btn " + (showTradingView ? 'btn-danger' : 'btn-outline-secondary')}
                  onClick={() => setShowTradingView((s) => !s)}
                >
                  {showTradingView ? 'Hide TradingView' : 'Show TradingView'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderChart()}
    </div>
  );
};

// Helper functions for TradingView interactions. They try to access the active chart exposed by the widget.
function addSMA() {
  try {
    const widget = window.tvWidget || (window.TradingView && window.TradingView.widget);
    const chart = window.tvWidget?.activeChart?.() || (widget && widget.activeChart && widget.activeChart());
    if (!chart || !chart.createStudy) return;
    chart.createStudy('Moving Average', false, false, [50]);
  } catch (e) {
    console.error('Add SMA failed', e);
  }
}

function addRSI() {
  try {
    const widget = window.tvWidget || (window.TradingView && window.TradingView.widget);
    const chart = window.tvWidget?.activeChart?.() || (widget && widget.activeChart && widget.activeChart());
    if (!chart || !chart.createStudy) return;
    chart.createStudy('RSI', false, false, [14]);
  } catch (e) {
    console.error('Add RSI failed', e);
  }
}

function removeStudies() {
  try {
    const widget = window.tvWidget || (window.TradingView && window.TradingView.widget);
    const chart = window.tvWidget?.activeChart?.() || (widget && widget.activeChart && widget.activeChart());
    if (!chart) return;
    if (chart.getAllStudies) {
      const studies = chart.getAllStudies() || [];
      studies.forEach((s) => {
        try { chart.removeStudy(s); } catch (e) {}
      });
    }
  } catch (e) {
    console.error('Remove studies failed', e);
  }
}

export default Charts;
